import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Settings, OfficeId } from '../../lib/types';
import { apiGetAgent, apiListTasks, apiGetAgentActivity } from '../../lib/api';
import { STATIC_AGENTS, getOffice } from '../../lib/config';
import ChatPanel from '../chat/ChatPanel';
import OfficeContextBanner from '../OfficeContextBanner';
import { Task } from '../../lib/types';

interface AgentWorkstationProps {
  agentId: string;
  officeId?: OfficeId;
  settings: Settings;
  onClose: () => void;
}

const STATUS_COLORS = {
  active: '#27C93F',
  idle: '#FFBD2E',
  blocked: '#FF5F56',
  offline: '#9CA3AF',
};

const MODEL_COLORS: Record<string, string> = {
  haiku: '#3498DB',
  sonnet: '#9B59B6',
  opus: '#E74C3C',
};

/**
 * AgentWorkstation — monitor-style panel for a specific agent.
 * Layout: header | [chat (left)] | [tasks / skills / activity (right)]
 */
export default function AgentWorkstation({ agentId, officeId, settings, onClose }: AgentWorkstationProps) {
  const [agent, setAgent] = useState<any>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activity, setActivity] = useState<Array<{ time: string; type: string; action: string }>>([]);
  const [loading, setLoading] = useState(true);

  const fetchActivity = () => {
    apiGetAgentActivity(agentId)
      .then(setActivity)
      .catch(() => setActivity([]));
  };

  useEffect(() => {
    Promise.all([
      apiGetAgent(agentId).catch(() => STATIC_AGENTS.find((a) => a.name === agentId) || null),
      apiListTasks(officeId).catch(() => []),
    ]).then(([agentData, allTasks]) => {
      setAgent(agentData);
      setTasks((allTasks as Task[]).filter((t) => t.agent_name === agentId));
    }).finally(() => setLoading(false));

    fetchActivity();
  }, [agentId, officeId]);

  const office = officeId ? getOffice(officeId) : undefined;
  const staticAgent = STATIC_AGENTS.find((a) => a.name === agentId);
  const displayAgent = agent || staticAgent;
  const status = displayAgent?.status || 'idle';
  const statusColor = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || '#9CA3AF';
  const model = displayAgent?.model || 'sonnet';
  const modelColor = MODEL_COLORS[model.toLowerCase()] || '#9B59B6';

  const agentWebhook = settings.agents?.[agentId]?.chatWebhookUrl;
  const agentPollUrl = settings.agents?.[agentId]?.chatPollUrl;

  const skills = Array.isArray(displayAgent?.skills)
    ? displayAgent.skills
    : [];

  const taskStats = {
    todo: tasks.filter((t) => t.column === 'todo').length,
    active: tasks.filter((t) => t.column === 'inprogress').length,
    done: tasks.filter((t) => t.column === 'done').length,
    blocked: tasks.filter((t) => t.column === 'review').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900 rounded-xl">
        <div className="h-6 w-6 rounded-full border-2 border-gray-700 border-t-gray-300 animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full h-full rounded-xl overflow-hidden shadow-lg panel-enter"
      style={{ backgroundColor: '#1a1a2e', border: '1px solid #2d2d44' }}
      data-testid={`agent-workstation-${agentId}`}
    >
      {/* Workstation Header */}
      <div
        className="flex items-center justify-between px-5 py-3 border-b shrink-0"
        style={{ backgroundColor: '#16213e', borderColor: '#2d2d44' }}
      >
        {/* Traffic lights */}
        <div className="flex items-center gap-1.5 w-16">
          <button
            onClick={onClose}
            className="w-3 h-3 rounded-full hover:opacity-80 transition-opacity"
            style={{ backgroundColor: '#FF5F56' }}
            data-testid="workstation-close-button"
          />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#FFBD2E' }} />
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#27C93F' }} />
        </div>

        {/* Agent identity */}
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
            style={{ backgroundColor: office?.color || '#4B5563' }}
          >
            {agentId.charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-white">{agentId}</span>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} title={status} />
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <OfficeContextBanner officeId={officeId} />
              <span
                className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
                style={{ backgroundColor: modelColor }}
              >
                {model}
              </span>
              {displayAgent?.type && (
                <span className="text-[10px] text-gray-500">{displayAgent.type.replace(/_/g, ' ')}</span>
              )}
            </div>
          </div>
        </div>

        <div className="w-16 text-right">
          <span className="text-[10px] text-gray-500 capitalize font-medium">{status}</span>
        </div>
      </div>

      {/* Workstation Body */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* LEFT: Chat */}
        <div
          className="w-3/5 flex flex-col border-r"
          style={{ borderColor: '#2d2d44', backgroundColor: '#0f0f1a' }}
          data-testid="workstation-chat-section"
        >
          <div className="px-4 py-2 border-b" style={{ borderColor: '#2d2d44' }}>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Direct Chat</span>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="h-full [&>div]:h-full">
              <ChatPanel
                conversationId={`agent:${agentId}`}
                agentName={agentId}
                webhookUrl={agentWebhook}
                pollUrl={agentPollUrl}
                compact
              />
            </div>
          </div>
        </div>

        {/* RIGHT: Tasks / Skills / Activity */}
        <div className="w-2/5 flex flex-col overflow-hidden">
          {/* Tasks */}
          <div
            className="border-b flex flex-col"
            style={{ borderColor: '#2d2d44', maxHeight: '35%' }}
            data-testid="workstation-tasks-section"
          >
            <div className="px-4 py-2 border-b flex items-center justify-between shrink-0" style={{ borderColor: '#2d2d44' }}>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Tasks</span>
              <div className="flex gap-2 text-[10px]">
                <span style={{ color: '#27C93F' }}>{taskStats.active} active</span>
                <span style={{ color: '#FFBD2E' }}>{taskStats.todo} todo</span>
                {taskStats.blocked > 0 && <span style={{ color: '#FF5F56' }}>{taskStats.blocked} review</span>}
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-1.5">
              {tasks.length === 0 ? (
                <p className="text-[10px] text-gray-600 px-1">No tasks assigned</p>
              ) : (
                tasks.slice(0, 8).map((task) => (
                  <TaskRow key={task.id} task={task} />
                ))
              )}
            </div>
          </div>

          {/* Skills */}
          <div
            className="border-b flex flex-col"
            style={{ borderColor: '#2d2d44', maxHeight: '35%' }}
            data-testid="workstation-skills-section"
          >
            <div className="px-4 py-2 border-b shrink-0" style={{ borderColor: '#2d2d44' }}>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                Skills ({skills.length})
              </span>
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-1.5">
              {skills.length === 0 ? (
                <p className="text-[10px] text-gray-600 px-1">No skills listed</p>
              ) : (
                skills.map((skill: string, i: number) => (
                  <div key={i} className="flex items-center gap-2 py-1 px-2 rounded" style={{ backgroundColor: '#1a1a2e' }}>
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-[10px] text-gray-400 font-medium">{skill}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Activity Log */}
          <div className="flex flex-col flex-1 overflow-hidden" data-testid="workstation-activity-section">
            <div className="px-4 py-2 border-b shrink-0 flex items-center justify-between" style={{ borderColor: '#2d2d44' }}>
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Activity Log</span>
              <button
                onClick={fetchActivity}
                className="text-gray-600 hover:text-gray-400 transition-colors"
                title="Refresh activity"
                data-testid="workstation-refresh-activity"
              >
                <RefreshCw size={10} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-2">
              {activity.length === 0 ? (
                <p className="text-[10px] text-gray-600 px-1">No activity yet</p>
              ) : (
                activity.map((entry, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[9px] text-gray-600 shrink-0 mt-0.5 font-mono whitespace-nowrap">
                      {relativeTime(entry.time)}
                    </span>
                    <span className="text-[10px] text-gray-400 leading-snug">{entry.action}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Task Row ──────────────────────────────────────────────────────────────────

const TASK_STATUS_STYLE = {
  todo: { color: '#6B7280', label: 'todo' },
  inprogress: { color: '#27C93F', label: 'active' },
  review: { color: '#FFBD2E', label: 'review' },
  done: { color: '#9CA3AF', label: 'done' },
};

function TaskRow({ task }: { task: Task }) {
  const style = TASK_STATUS_STYLE[task.column as keyof typeof TASK_STATUS_STYLE] || TASK_STATUS_STYLE.todo;
  return (
    <div className="flex items-center gap-2 py-1 px-2 rounded" style={{ backgroundColor: '#1a1a2e' }}>
      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: style.color }} />
      <span className="text-[10px] text-gray-400 truncate flex-1">{task.title}</span>
      <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: style.color }}>
        {style.label}
      </span>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return '';
  }
}
